import jsPDF from "jspdf";
import QRCode from "qrcode";
import {
  CertificatePage,
  CertificadoElemento,
  CertificadoImagem,
  CertificadoQrCode,
  CertificadoTexto,
} from "../types/CertificateTemplate";

type PdfTextToken = {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  lineBreak?: boolean;
};

const isTextoElemento = (element: CertificadoElemento): element is CertificadoTexto => {
  return element.type === "html_text";
};

const isImagemElemento = (element: CertificadoElemento): element is CertificadoImagem => {
  return element.type === "image";
};

const isQrCodeElemento = (element: CertificadoElemento): element is CertificadoQrCode => {
  return element.type === "validation_qrcode";
};

export const getPdfPageSize = (orientation: "landscape" | "portrait") => {
  return orientation === "landscape"
    ? { width: 900, height: 636 }
    : { width: 636, height: 900 };
};

const getPdfFontFamily = (fontFamily: string) => {
  if (fontFamily.toLowerCase().includes("montserrat")) return "Montserrat";
  if (fontFamily.toLowerCase().includes("poppins")) return "Poppins";
  if (fontFamily.toLowerCase().includes("times")) return "times";
  if (fontFamily.toLowerCase().includes("courier")) return "courier";
  return "helvetica";
};

const getPdfFontStyle = (token: PdfTextToken) => {
  if (token.bold && token.italic) return "bolditalic";
  if (token.bold) return "bold";
  if (token.italic) return "italic";
  return "normal";
};

const getImageFormat = (dataUrl: string) => {
  if (dataUrl.includes("image/jpeg") || dataUrl.includes("image/jpg")) return "JPEG";
  if (dataUrl.includes("image/webp")) return "WEBP";
  return "PNG";
};

const numberOrDefault = (value: number | undefined, fallback: number) => {
  return typeof value === "number" && !Number.isNaN(value) ? value : fallback;
};

export const buildCertificateVariableMap = (
  dados: Record<string, string>,
  codigo?: string,
  impressoEm?: string,
) => {
  const nextMap: Record<string, string> = { ...(dados || {}) };

  if (codigo) {
    nextMap["{{codigoValidacao}}"] = nextMap["{{codigoValidacao}}"] || codigo;
    nextMap["{{codigo_certificado}}"] = nextMap["{{codigo_certificado}}"] || codigo;
  }

  if (impressoEm) {
    nextMap["{{dataEmissao}}"] = nextMap["{{dataEmissao}}"] || impressoEm;
    nextMap["{{data_emissao_certificado}}"] =
      nextMap["{{data_emissao_certificado}}"] || impressoEm;
  }

  return nextMap;
};

export const replaceCertificateVariables = (
  text: string | null | undefined,
  variableMap: Record<string, string>,
) => {
  const safeText = text ?? "";

  return Object.entries(variableMap).reduce(
    (result, [variable, value]) => result.replaceAll(variable, value),
    safeText,
  );
};

export const replaceCertificateVariablesInHtml = (
  html: string,
  variableMap: Record<string, string>,
) => {
  return replaceCertificateVariables(html, variableMap);
};

const buildQrValidationUrl = (validationUrl: string, variableMap: Record<string, string>) => {
  const renderedUrl = replaceCertificateVariables(validationUrl, variableMap);

  try {
    return new URL(renderedUrl, window.location.origin).toString();
  } catch {
    return renderedUrl;
  }
};

export const hydrateCertificatePage = async (
  page: CertificatePage,
  variableMap: Record<string, string>,
): Promise<CertificatePage> => {
  const elements = await Promise.all(
    page.elements.map(async (elemento) => {
      if (!isQrCodeElemento(elemento)) {
        return elemento;
      }

      const validationUrl = elemento.validationUrl || "/validar-certificado?chave={{codigo_certificado}}";
      const qrCodeDataUrl =
        elemento.qrCodeDataUrl ||
        (await QRCode.toDataURL(buildQrValidationUrl(validationUrl, variableMap), {
          margin: 1,
          width: numberOrDefault(elemento.qrSize, 100),
        }));

      return {
        ...elemento,
        qrCodeDataUrl,
      };
    }),
  );

  return {
    ...page,
    elements,
  };
};

const htmlToPdfTokens = (html: string, variableMap: Record<string, string>): PdfTextToken[] => {
  const documentHtml = new DOMParser().parseFromString(html || "", "text/html");
  const tokens: PdfTextToken[] = [];

  const walk = (
    node: ChildNode,
    marks: { bold: boolean; italic: boolean; underline: boolean },
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent) {
        tokens.push({
          text: replaceCertificateVariables(node.textContent, variableMap),
          ...marks,
        });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const nextMarks = {
      bold: marks.bold || ["strong", "b"].includes(tag),
      italic: marks.italic || ["em", "i"].includes(tag),
      underline: marks.underline || tag === "u",
    };

    if (tag === "br") {
      tokens.push({ text: "", ...marks, lineBreak: true });
      return;
    }

    Array.from(element.childNodes).forEach((child) => walk(child, nextMarks));

    if (["p", "div"].includes(tag)) {
      tokens.push({ text: "", ...marks, lineBreak: true });
    }
  };

  Array.from(documentHtml.body.childNodes).forEach((child) =>
    walk(child, { bold: false, italic: false, underline: false }),
  );

  while (tokens[tokens.length - 1]?.lineBreak) {
    tokens.pop();
  }

  return tokens.length ? tokens : [{ text: "", bold: false, italic: false, underline: false }];
};

const renderHtmlTextInPdf = (
  doc: jsPDF,
  element: CertificadoTexto,
  variableMap: Record<string, string>,
) => {
  const tokens = htmlToPdfTokens(element.contentHtml, variableMap);
  const fontFamily = getPdfFontFamily(element.style.fontFamily);
  const fontSize = element.style.fontSize;
  const lineHeight = fontSize * 1.25;
  const maxWidth = numberOrDefault(element.size.width, 200);
  const positionX = numberOrDefault(element.position.x, 0);
  const positionY = numberOrDefault(element.position.y, 0);

  type LinePart = PdfTextToken & { width: number };
  const lines: LinePart[][] = [[]];
  const lineWidths: number[] = [0];

  const pushLine = () => {
    lines.push([]);
    lineWidths.push(0);
  };

  tokens.forEach((token) => {
    if (token.lineBreak) {
      pushLine();
      return;
    }

    const parts = token.text.split(/(\s+)/);
    parts.forEach((part) => {
      if (!part) return;

      doc.setFont(fontFamily, getPdfFontStyle(token));
      doc.setFontSize(fontSize);
      const partWidth = doc.getTextWidth(part);
      const currentLineIndex = lines.length - 1;
      const currentWidth = lineWidths[currentLineIndex];
      const isOnlySpace = part.trim().length === 0;

      if (!isOnlySpace && currentWidth > 0 && currentWidth + partWidth > maxWidth) {
        pushLine();
      }

      const nextLineIndex = lines.length - 1;
      lines[nextLineIndex].push({ ...token, text: part, width: partWidth });
      lineWidths[nextLineIndex] += partWidth;
    });
  });

  lines.forEach((line, lineIndex) => {
    const lineWidth = lineWidths[lineIndex];
    const offsetX =
      element.style.align === "center"
        ? (maxWidth - lineWidth) / 2
        : element.style.align === "right"
          ? maxWidth - lineWidth
          : 0;
    let cursorX = positionX + Math.max(offsetX, 0);
    const cursorY = positionY + fontSize + lineIndex * lineHeight;

    line.forEach((part) => {
      doc.setFont(fontFamily, getPdfFontStyle(part));
      doc.setFontSize(fontSize);
      doc.setTextColor(element.style.color);
      doc.text(part.text, cursorX, cursorY);

      if (part.underline && part.text.trim()) {
        doc.setDrawColor(element.style.color);
        doc.line(cursorX, cursorY + 2, cursorX + part.width, cursorY + 2);
      }

      cursorX += part.width;
    });
  });
};

const renderImageInPdf = (doc: jsPDF, element: CertificadoImagem) => {
  doc.addImage(
    element.dataUrl,
    getImageFormat(element.dataUrl),
    numberOrDefault(element.position.x, 0),
    numberOrDefault(element.position.y, 0),
    numberOrDefault(element.size.width, 100),
    numberOrDefault(element.size.height, 100),
  );
};

const renderQrCodeInPdf = (
  doc: jsPDF,
  element: CertificadoQrCode,
  variableMap: Record<string, string>,
) => {
  doc.addImage(
    element.qrCodeDataUrl,
    getImageFormat(element.qrCodeDataUrl),
    numberOrDefault(element.position.x, 0),
    numberOrDefault(element.position.y, 0),
    numberOrDefault(element.qrSize, 100),
    numberOrDefault(element.qrSize, 100),
  );

  const fontFamily = getPdfFontFamily(element.style.fontFamily);
  const textX =
    element.textPosition === "right"
      ? numberOrDefault(element.position.x, 0) + numberOrDefault(element.qrSize, 100) + 10
      : numberOrDefault(element.position.x, 0);
  const textY =
    element.textPosition === "right"
      ? numberOrDefault(element.position.y, 0) + element.style.fontSize
      : numberOrDefault(element.position.y, 0) + numberOrDefault(element.qrSize, 100) + element.style.fontSize + 8;
  const textWidth =
    element.textPosition === "right"
      ? Math.max(numberOrDefault(element.size.width, 300) - numberOrDefault(element.qrSize, 100) - 10, 80)
      : numberOrDefault(element.size.width, 300);
  const lineHeight = element.style.fontSize * 1.35;

  doc.setFont(fontFamily, "normal");
  doc.setFontSize(element.style.fontSize);
  doc.setTextColor(element.style.color);
  doc.text(replaceCertificateVariables(element.institutionText, variableMap), textX, textY);

  doc.setFont(fontFamily, "normal");
  doc.text("Chave de validacao: ", textX, textY + lineHeight);
  const keyLabelWidth = doc.getTextWidth("Chave de validacao: ");
  doc.setFont(fontFamily, "bold");
  doc.text(
    `${replaceCertificateVariables(element.validationKey, variableMap)}. Data de emissao: ${replaceCertificateVariables(element.issueDate, variableMap)}`,
    textX + keyLabelWidth,
    textY + lineHeight,
  );

  doc.setFont(fontFamily, "normal");
  const instructionLines = doc.splitTextToSize(
    replaceCertificateVariables(element.instructionText, variableMap),
    textWidth,
  );
  doc.text(instructionLines, textX, textY + lineHeight * 2);
};

const addBackgroundToPdf = (doc: jsPDF, page: CertificatePage, orientation: "landscape" | "portrait") => {
  if (!page.background) {
    return;
  }

  const { width: pageWidth, height: pageHeight } = getPdfPageSize(orientation);
  const imageFormat = getImageFormat(page.background.dataUrl);

  if (page.background.fit === "contain" || page.background.fit === "cover") {
    const imageProps = doc.getImageProperties(page.background.dataUrl);
    const imageRatio = imageProps.width / imageProps.height;
    const pageRatio = pageWidth / pageHeight;
    const shouldFitByWidth =
      page.background.fit === "contain" ? imageRatio < pageRatio : imageRatio > pageRatio;
    const imageWidth = shouldFitByWidth ? pageWidth : pageHeight * imageRatio;
    const imageHeight = shouldFitByWidth ? pageWidth / imageRatio : pageHeight;
    const imageX = (pageWidth - imageWidth) / 2;
    const imageY = (pageHeight - imageHeight) / 2;

    doc.addImage(page.background.dataUrl, imageFormat, imageX, imageY, imageWidth, imageHeight);
    return;
  }

  doc.addImage(page.background.dataUrl, imageFormat, 0, 0, pageWidth, pageHeight);
};

export const renderCertificatePageToPdf = (
  doc: jsPDF,
  page: CertificatePage,
  orientation: "landscape" | "portrait",
  variableMap: Record<string, string>,
) => {
  addBackgroundToPdf(doc, page, orientation);

  page.elements.forEach((elemento) => {
    if (isTextoElemento(elemento)) {
      renderHtmlTextInPdf(doc, elemento, variableMap);
      return;
    }

    if (isImagemElemento(elemento)) {
      renderImageInPdf(doc, elemento);
      return;
    }

    renderQrCodeInPdf(doc, elemento, variableMap);
  });
};
